import { useEffect, useState } from 'react';

import { getCurrentAWSCognitoGroups } from '../../../services/auth';
import { CacheKey } from '../../common/constants';
import useUserId from './useUserId';

const readCachedGroups = (): string[] | undefined => {
  const cached = localStorage.getItem(CacheKey.UserGroups);

  return cached ? (JSON.parse(cached) as string[]) : undefined;
};

/**
 * The reader's Cognito groups, or `undefined` until they are known.
 *
 * An empty list is a reader in no group, which every gate refuses; `undefined`
 * is a list not yet read, which none may refuse on.
 */
const useUserGroups = () => {
  const userId = useUserId();
  const [userGroups, setUserGroups] = useState<string[] | undefined>(readCachedGroups);

  // Groups belong to an identity, so they are re-read whenever the signed-in
  // user changes rather than cached indefinitely from the first sign-in
  useEffect(() => {
    // There is no identity to read groups for until the session resolves
    if (!userId) {
      return;
    }

    const fetchUserGroups = async () => {
      const groups = await getCurrentAWSCognitoGroups();
      // A session that cannot be read leaves the cached groups in place: every
      // group-gated part of the app treats an empty list as "no access", so
      // overwriting them logs the user out of the UI while they stay signed in
      if (groups === undefined) {
        return;
      }

      // Every consumer holds its own state but they share one cache entry, so
      // the groups are adopted whether or not this hook is the one that cached
      // them; the comparison is against this hook's own state, which keeps the
      // reference stable when nothing changed
      localStorage.setItem(CacheKey.UserGroups, JSON.stringify(groups));
      setUserGroups(current =>
        JSON.stringify(current) === JSON.stringify(groups) ? current : groups
      );
    };

    fetchUserGroups();
  }, [userId]);

  return userGroups;
};

export default useUserGroups;
